import { useMemo } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import type { CountryWithInstitutions } from '../lib/countries-with-institutions'

interface WizardCountryStepProps {
  countries: CountryWithInstitutions[]
  search: string
  onSearchChange: (value: string) => void
  onSelect: (country: CountryWithInstitutions) => void
}

export function WizardCountryStep({ countries, search, onSearchChange, onSelect }: WizardCountryStepProps) {
  const searchLower = search.toLowerCase()

  const filteredCountries = useMemo(() => {
    // All active countries render here, even ones without any institution — the
    // non-Open-Banking connectivities now pick their institution from a global
    // dropdown inside the form, so a country no longer needs institutions to be usable.
    if (!searchLower) return countries
    return countries.filter((country) =>
      country.CountryName.toLowerCase().includes(searchLower)
      || country.Institutions.some((institution) => institution.Name.toLowerCase().includes(searchLower)),
    )
  }, [countries, searchLower])

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search country…"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {filteredCountries.map((country) => (
          <button
            key={country.CountryCode}
            type="button"
            className="flex flex-col items-center gap-2 rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer text-center"
            onClick={() => onSelect(country)}
          >
            <span className={`fi fi-${country.CountryCode.toLowerCase()} text-3xl`} />
            <span className="text-xs leading-tight line-clamp-2">{country.CountryName}</span>
          </button>
        ))}
        {filteredCountries.length === 0 && (
          <p className="col-span-full text-sm text-muted-foreground text-center py-8">
            No countries found.
          </p>
        )}
      </div>
    </div>
  )
}
